import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { DataTable, type Column } from '../../components/table/DataTable';
import { listCountries, type CountryListRow } from '../../services/countryMasters';
import { MD_PERMISSIONS as MD } from '../../rbac/metadataPermissions';
import { PermissionGate } from '../../rbac/guards';
import { consumeFlashToast } from '../../utils/flashToast';
import { useAuth } from '../../contexts/AuthContext';

const EMPTY = '\u2014';

function filterRows(rows: CountryListRow[], name: string): CountryListRow[] {
  const nameLower = name.trim().toLowerCase();
  if (!nameLower) return rows;
  return rows.filter((row) => row.name.toLowerCase().includes(nameLower));
}

export default function CountriesPage() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [toast, setToast] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [allRows, setAllRows] = useState<CountryListRow[]>([]);
  const [reloadToken, setReloadToken] = useState(0);

  const [draftName, setDraftName] = useState('');
  const [name, setName] = useState('');

  const [uiPage, setUiPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  useEffect(() => {
    const flash = consumeFlashToast();
    if (flash?.message) {
      setToast(flash.message);
      window.setTimeout(() => setToast(null), 3200);
    }
  }, []);

  const loadList = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await listCountries();
      if (!res.success) throw new Error(res.message);
      setAllRows(res.data);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : t('metadata.countries.loadFailed'));
      setAllRows([]);
    } finally {
      setLoading(false);
    }
  }, [reloadToken, t]);

  useEffect(() => {
    if (!user) return;
    void loadList();
  }, [user?.id, loadList]);

  const rows = useMemo(() => filterRows(allRows, name), [allRows, name]);

  const totalPages = Math.max(1, Math.ceil(rows.length / pageSize));
  const safePage = Math.min(uiPage, totalPages);
  const pageRows = rows.slice((safePage - 1) * pageSize, safePage * pageSize);

  const columns = useMemo<Column<CountryListRow>[]>(() => {
    return [
      {
        key: 'name',
        header: t('metadata.countries.countryName'),
        getValue: (r) => r.name,
        render: (r) => <span style={{ fontWeight: 600 }}>{r.name || EMPTY}</span>,
      },
      {
        key: 'code',
        header: t('metadata.countries.countryCode'),
        getValue: (r) => r.code,
        render: (r) => <span style={{ color: 'var(--t2)' }}>{r.code || EMPTY}</span>,
      },
      {
        key: 'description',
        header: t('metadata.countries.description'),
        getValue: (r) => r.description,
        render: (r) => r.description || EMPTY,
      },
      {
        key: 'active',
        header: t('common.status'),
        getValue: (r) => (r.active ? t('common.active') : t('common.inactive')),
        render: (r) => (
          <span className={`al-pill al-pill-${r.active ? 'file-d' : 'user-d'}`}>
            {r.active ? t('common.active') : t('common.inactive')}
          </span>
        ),
      },
      {
        key: 'actions',
        header: t('common.actions'),
        getValue: () => '',
        searchable: false,
        sortable: false,
        render: (r) => (
          <div className="act-wrap">
            <PermissionGate requirement={{ anyPermissions: [MD.MANAGE_MASTER_DATA] }}>
              <Link
                className="act-b"
                to={`/metadata/countries/${encodeURIComponent(r.id)}/edit`}
                data-tip={t('metadata.countries.updateCountry')}
                aria-label={t('metadata.countries.updateCountry')}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                  <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                </svg>
              </Link>
            </PermissionGate>
          </div>
        ),
      },
    ];
  }, [t]);

  const resetFilters = () => {
    setDraftName('');
    setName('');
    setUiPage(1);
    setPageSize(10);
    setReloadToken((n) => n + 1);
  };

  const applyFilters = () => {
    setName(draftName.trim());
    setUiPage(1);
  };

  return (
    <>
      <div className="bc">
        <span>{t('nav.groups.metadata')}</span>
        <span className="bc-sep">/</span>
        {t('metadata.countries.title')}
      </div>

      <div className="page-hdr">
        <div>
          <div className="pg-title">{t('metadata.countries.title')}</div>
        </div>
        <PermissionGate requirement={{ anyPermissions: [MD.MANAGE_MASTER_DATA] }}>
          <Link to="/metadata/countries/new" className="btn-create" style={{ textDecoration: 'none' }}>
            {t('metadata.countries.addCountry')}
          </Link>
        </PermissionGate>
      </div>

      {toast ? <div className="si-ok" style={{ marginBottom: 12 }}>{toast}</div> : null}

      <div className="flt-card">
        <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end' }}>
          <div className="flt-f" style={{ width: '50%' }}>
            <div className="flt-l">{t('metadata.countries.countryName')}</div>
            <input
              className="al-inp"
              value={draftName}
              placeholder={t('metadata.countries.countryNamePlaceholder')}
              onChange={(e) => setDraftName(e.target.value)}
            />
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="flt-btn" type="button" onClick={resetFilters}>
              {t('common.reset')}
            </button>
            <button className="flt-btn pr" type="button" onClick={applyFilters}>
              {t('common.search')}
            </button>
          </div>
        </div>
      </div>

      {error ? <div className="si-err">{error}</div> : null}

      <DataTable
        title={t('metadata.countries.tableTitle')}
        loading={loading}
        enableHeaderSearch={false}
        emphasizedHeader
        columns={columns}
        rows={pageRows}
        rowKey={(r) => r.id}
        defaultRowsPerPage={10}
        rowsPerPageOptions={[5, 10, 15, 25]}
        footerCountNoun={t('metadata.countries.footerCountNoun')}
        previousLabel={t('common.previous')}
        nextLabel={t('common.next')}
        serverPaging={{
          totalCount: rows.length,
          totalPages,
          page: safePage,
          pageSize,
          onPageChange: (p) => setUiPage(p),
          onPageSizeChange: (n) => {
            setPageSize(n);
            setUiPage(1);
          },
        }}
      />
    </>
  );
}
